/**
 * MaintenanceAlertContext - Real-time Maintenance Alert System
 *
 * Context สำหรับจัดการการแจ้งเตือน maintenance case ใหม่แบบ real-time
 * ทำงานร่วมกับ WebSocketContext (shared connection) แทนการสร้าง connection แยก
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useWebSocketContext } from './WebSocketContext';
import { isClosedAtBirthAlert } from '../utils/maintenanceAlertGate';
import {
  useMaintenanceAlertSettings,
  MaintenanceAlertSettings,
  shouldNotifyForPriority,
} from '../hooks/useMaintenanceAlertSettings';

// =============== Types ===============

export interface MaintenanceAlert {
  id: string;
  notificationId: number;
  maintenanceId: number;
  deviceCode: string;
  deviceId: number | null;
  issueType: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  siteName: string | null;
  siteId: number | null;
  description: string | null;
  status: string | null;
  reportedBy: string | null;
  timestamp: string;
  isNew: boolean;
  isViewed: boolean;
}

export interface MaintenanceAlertContextType {
  // State
  alerts: MaintenanceAlert[];
  newAlertIds: Set<number>;
  unreadCount: number;
  latestAlert: MaintenanceAlert | null;
  isConnected: boolean;
  connectionError: string | null;

  // Settings
  settings: MaintenanceAlertSettings;
  updateSettings: (partial: Partial<MaintenanceAlertSettings>) => void;
  updatePriority: (priority: keyof MaintenanceAlertSettings['priorities'], enabled: boolean) => void;
  resetSettingsToDefaults: () => void;

  // Actions
  dismissAlert: (id: string) => void;
  dismissAll: () => void;
  markAsViewed: (maintenanceId: number) => void;
  markAllAsViewed: () => void;
  playAlertSound: () => void;
  clearLatestAlert: () => void;
  startTitleFlash: (message: string) => void;
  stopTitleFlash: () => void;
  testPopup: () => void;

  // WebSocket controls (kept for API compatibility)
  connect: () => void;
  disconnect: () => void;
}

// =============== Context ===============

const MaintenanceAlertContext = createContext<MaintenanceAlertContextType | null>(null);

// =============== Sound Generator ===============
// Browser autoplay policy: AudioContext must be created/resumed inside a user gesture.
// Strategy: create context lazily on first user interaction, then reuse + resume before play.

let _sharedAudioCtx: AudioContext | null = null;
let _audioUnlocked = false;

function _unlockAudio() {
  try {
    const Ctor = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return;
    if (!_sharedAudioCtx || _sharedAudioCtx.state === 'closed') {
      _sharedAudioCtx = new Ctor();
    }
    if (_sharedAudioCtx.state === 'suspended') {
      _sharedAudioCtx.resume();
    }
    // Play a silent buffer to fully unlock on iOS/Safari
    const buf = _sharedAudioCtx.createBuffer(1, 1, 22050);
    const src = _sharedAudioCtx.createBufferSource();
    src.buffer = buf;
    src.connect(_sharedAudioCtx.destination);
    src.start(0);
    _audioUnlocked = true;
  } catch { /* ignore */ }
}

// Register on many gesture types so it fires as early as possible
if (typeof window !== 'undefined') {
  const _events = ['click', 'touchstart', 'keydown', 'mousedown', 'pointerdown'];
  const _onGesture = () => {
    _unlockAudio();
    _events.forEach(e => window.removeEventListener(e, _onGesture, true));
  };
  _events.forEach(e => window.addEventListener(e, _onGesture, { capture: true, once: true }));
}

const createAlertSound = () => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  return () => {
    try {
      // Ensure context is ready (resume if suspended)
      if (!_audioUnlocked) _unlockAudio();
      const audioContext = _sharedAudioCtx;
      if (!audioContext || audioContext.state === 'closed') return;
      if (audioContext.state === 'suspended') {
        audioContext.resume().catch(() => {});
      }

      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Siren-like alert sound pattern - 10 cycles
      oscillator.type = 'sine';

      const cycleTime = 0.5;
      const cycles = 10;
      const totalDuration = cycleTime * cycles;

      for (let i = 0; i < cycles; i++) {
        const cycleStart = audioContext.currentTime + (i * cycleTime);
        oscillator.frequency.setValueAtTime(800, cycleStart);
        oscillator.frequency.linearRampToValueAtTime(1200, cycleStart + (cycleTime / 2));
        oscillator.frequency.linearRampToValueAtTime(800, cycleStart + cycleTime);
      }

      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.25, audioContext.currentTime + 0.05);
      gainNode.gain.setValueAtTime(0.25, audioContext.currentTime + totalDuration - 0.1);
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + totalDuration);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + totalDuration);

      setTimeout(() => {
        oscillator.disconnect();
        gainNode.disconnect();
      }, (totalDuration + 0.1) * 1000);
    } catch (error) {
      console.warn('Failed to play alert sound:', error);
    }
  };
};

// =============== Provider ===============

interface MaintenanceAlertProviderProps {
  children: React.ReactNode;
}

// สถานะที่ถือว่า "จบเคสแล้ว" — ไม่ต้อง highlight เรียกร้องความสนใจอีก
const CLOSED_STATUSES = new Set(['CLOSED', 'CANCELLED']);

export const MaintenanceAlertProvider: React.FC<MaintenanceAlertProviderProps> = ({
  children,
}) => {
  const {
    settings,
    updateSettings,
    updatePriority,
    resetToDefaults: resetSettingsToDefaults,
    isLoaded: settingsLoaded,
  } = useMaintenanceAlertSettings();

  const [alerts, setAlerts] = useState<MaintenanceAlert[]>([]);
  const [newAlertIds, setNewAlertIds] = useState<Set<number>>(new Set());
  const [latestAlert, setLatestAlert] = useState<MaintenanceAlert | null>(null);
  const settingsRef = useRef(settings);
  const originalTitleRef = useRef<string>(document.title);
  const titleFlashIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Update ref when settings change so message handler always sees latest settings
  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  // Get shared WebSocket connection — no new connection is created here
  const {
    isConnected,
    connectionError,
    connect,
    disconnect,
    subscribe,
  } = useWebSocketContext();

  // =============== Tab Title Flash ===============

  const startTitleFlash = useCallback((alertMessage: string) => {
    if (titleFlashIntervalRef.current) {
      clearInterval(titleFlashIntervalRef.current);
    }

    if (!originalTitleRef.current || originalTitleRef.current.includes('🚨')) {
      originalTitleRef.current = 'KRP System | ระบบจัดการข้อมูลการเช่าอุปกรณ์';
    }

    let isAlert = true;
    titleFlashIntervalRef.current = setInterval(() => {
      if (isAlert) {
        document.title = `🚨 ${alertMessage}`;
      } else {
        document.title = originalTitleRef.current;
      }
      isAlert = !isAlert;
    }, 1000);

    setTimeout(() => {
      stopTitleFlash();
    }, 30000);
  }, []);

  const stopTitleFlash = useCallback(() => {
    if (titleFlashIntervalRef.current) {
      clearInterval(titleFlashIntervalRef.current);
      titleFlashIntervalRef.current = null;
    }
    document.title = originalTitleRef.current;
  }, []);

  // Stop flashing when user returns to tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        stopTitleFlash();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopTitleFlash();
    };
  }, [stopTitleFlash]);

  const playAlertSound = useCallback(createAlertSound(), []);

  // =============== WebSocket message handler ===============

  useEffect(() => {
    // Only subscribe after settings are loaded so priority filters work correctly
    if (!settingsLoaded) return;

    const handleMessage = (message: any) => {
      // Only handle maintenance-related messages
      if (
        message.type !== 'maintenance_new' &&
        message.type !== 'maintenance_update' &&
        message.type !== 'maintenance_assigned' &&
        message.type !== 'maintenance_completed'
      ) {
        return;
      }

      // feedback ทิวากร 7 ก.ย. 2026: เคสที่สร้างมาเป็น "ปิด/ยกเลิก" ไม่ใช่งานค้าง — ห้าม highlight/popup/เสียง
      // (backend เลิกยิงแล้ว แต่กันอีกชั้นเผื่อ backend รุ่นก่อนหรือ event จากแหล่งอื่น)
      if (isClosedAtBirthAlert(message)) {
        console.log(`MaintenanceAlertContext: Skipping 'new' alert for case created as ${message.data?.status}`);
        return;
      }

      const data = message.data || {};
      const priority = (data.priority || 'normal').toLowerCase();
      const currentSettings = settingsRef.current;

      if (!shouldNotifyForPriority(priority, currentSettings)) {
        console.log(`MaintenanceAlertContext: Skipping alert for priority '${priority}' (filtered out)`);
        return;
      }

      const newAlert: MaintenanceAlert = {
        id: `alert-${message.notification_id || Date.now()}`,
        notificationId: message.notification_id,
        maintenanceId: data.maintenance_id,
        deviceCode: data.device_code || `Device #${data.device_id}`,
        deviceId: data.device_id,
        issueType: data.issue_type || 'ซ่อมบำรุง',
        priority: priority,
        siteName: data.site_name,
        siteId: data.site_id,
        description: data.description,
        status: data.status,
        reportedBy: data.reported_by,
        timestamp: message.timestamp || new Date().toISOString(),
        isNew: true,
        isViewed: false,
      };

      setAlerts((prev) => {
        const exists = prev.some(
          (a) => a.maintenanceId === newAlert.maintenanceId && a.notificationId === newAlert.notificationId
        );
        if (exists) return prev;
        return [newAlert, ...prev].slice(0, 50);
      });

      // user feedback ทิวากร 2026-09-08: เปิดหน้ารายการค้างไว้ เคสใหม่เข้ามาแล้ว highlight แดง
      // แต่ถ้าคนอื่นปิดเคสนั้นไป highlight ยังค้างจนกว่าจะ reload · เคสที่จบแล้วไม่ควรเรียกร้อง
      // ความสนใจอีก → ปลด highlight ทันทีที่ event บอกว่าสถานะเป็นสถานะปิดงาน
      if (message.type !== 'maintenance_new') {
        const closedStatus = String(data.status || '').toUpperCase();
        if (message.type === 'maintenance_completed' || CLOSED_STATUSES.has(closedStatus)) {
          setNewAlertIds((prev) => {
            if (!prev.has(newAlert.maintenanceId)) return prev;
            const next = new Set(prev);
            next.delete(newAlert.maintenanceId);
            return next;
          });
          setAlerts((prev) =>
            prev.map((a) =>
              a.maintenanceId === newAlert.maintenanceId
                ? { ...a, status: newAlert.status, isNew: false }
                : a
            )
          );
        }
      }

      if (message.type === 'maintenance_new') {
        setNewAlertIds((prev) => {
          const next = new Set(prev);
          next.add(newAlert.maintenanceId);
          return next;
        });

        if (currentSettings.popupEnabled) {
          setLatestAlert(newAlert);
        }

        if (currentSettings.soundEnabled) {
          playAlertSound();
        }

        // Browser Notification fallback — uses OS sound, not blocked by autoplay policy
        if ('Notification' in window) {
          if (Notification.permission === 'granted') {
            new Notification(`🔧 ${message.title || 'เคสซ่อมบำรุงใหม่'}`, {
              body: message.message || newAlert.description || '',
              icon: '/favicon.ico',
              tag: `maintenance-${newAlert.maintenanceId}`,
            });
          } else if (Notification.permission === 'default') {
            Notification.requestPermission();
          }
        }

        if (currentSettings.tabFlashEnabled && document.visibilityState === 'hidden') {
          startTitleFlash(`เคสซ่อมใหม่: ${newAlert.deviceCode}`);
        }
      }
    };

    return subscribe(handleMessage);
  }, [settingsLoaded, subscribe, playAlertSound, startTitleFlash]);

  // =============== Test popup ===============

  const testPopup = useCallback(() => {
    // testPopup available in all environments for admin testing
    const mockAlert: MaintenanceAlert = {
      id: `test-alert-${Date.now()}`,
      notificationId: 0,
      maintenanceId: 0,
      deviceCode: 'TEST-DEVICE-001',
      deviceId: null,
      issueType: 'ทดสอบการแจ้งเตือน',
      priority: 'high',
      siteName: 'โครงการทดสอบ',
      siteId: null,
      description: 'นี่คือการทดสอบ popup แจ้งเตือน',
      status: 'pending',
      reportedBy: 'System Test',
      timestamp: new Date().toISOString(),
      isNew: true,
      isViewed: false,
    };
    setLatestAlert(mockAlert);
  }, []);

  const unreadCount = alerts.filter((a) => !a.isViewed).length;

  // =============== Actions ===============

  const dismissAlert = useCallback((id: string) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setAlerts([]);
    setNewAlertIds(new Set());
  }, []);

  const markAsViewed = useCallback((maintenanceId: number) => {
    setNewAlertIds((prev) => {
      const next = new Set(prev);
      next.delete(maintenanceId);
      return next;
    });
    setAlerts((prev) =>
      prev.map((a) =>
        a.maintenanceId === maintenanceId ? { ...a, isViewed: true, isNew: false } : a
      )
    );
  }, []);

  const markAllAsViewed = useCallback(() => {
    setNewAlertIds(new Set());
    setAlerts((prev) => prev.map((a) => ({ ...a, isViewed: true, isNew: false })));
  }, []);

  const clearLatestAlert = useCallback(() => {
    setLatestAlert(null);
  }, []);

  // BUG-S2284-001: เดิมมี setTimeout ล้าง latestAlert หลัง popupDuration ตรงนี้อีกชั้น — ไม่รู้ว่าผู้ใช้กำลัง hover
  // อ่าน popup อยู่ → popup หายใต้เมาส์ (hover หยุดได้แค่แถบเวลา) · MiniPopupNotification ปิดตัวเองเมื่อแถบเวลาหมด
  // (onClose → clearLatestAlert) และ latestAlert ถูกตั้งเฉพาะตอน popupEnabled จึงไม่ต้องมีตัวจับเวลาซ้อน

  // =============== Context Value ===============

  const contextValue: MaintenanceAlertContextType = {
    alerts,
    newAlertIds,
    unreadCount,
    latestAlert,
    isConnected,
    connectionError,

    settings,
    updateSettings,
    updatePriority,
    resetSettingsToDefaults,

    dismissAlert,
    dismissAll,
    markAsViewed,
    markAllAsViewed,
    playAlertSound,
    clearLatestAlert,
    startTitleFlash,
    stopTitleFlash,
    testPopup,

    // Delegate to the shared connection
    connect,
    disconnect,
  };

  return (
    <MaintenanceAlertContext.Provider value={contextValue}>
      {children}
    </MaintenanceAlertContext.Provider>
  );
};

// =============== Hook ===============

// Safe default for when provider is not yet mounted (prevents app crash)
const safeDefault: MaintenanceAlertContextType = {
  alerts: [],
  newAlertIds: new Set(),
  unreadCount: 0,
  latestAlert: null,
  isConnected: false,
  connectionError: null,
  settings: {
    autoConnect: true,
    soundEnabled: true,
    tabFlashEnabled: true,
    popupEnabled: true,
    popupDuration: 10000,
    priorities: { urgent: true, high: true, normal: true, low: false },
  },
  updateSettings: () => {},
  updatePriority: () => {},
  resetSettingsToDefaults: () => {},
  dismissAlert: () => {},
  dismissAll: () => {},
  markAsViewed: () => {},
  markAllAsViewed: () => {},
  playAlertSound: () => {},
  clearLatestAlert: () => {},
  startTitleFlash: () => {},
  stopTitleFlash: () => {},
  testPopup: () => {},
  connect: () => {},
  disconnect: () => {},
};

export const useMaintenanceAlert = (): MaintenanceAlertContextType => {
  const context = useContext(MaintenanceAlertContext);
  return context ?? safeDefault;
};

export default MaintenanceAlertContext;
